import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1638864616275-9f0b291a2eb6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHRleHR1cmUlMjBkYXJrfGVufDB8fHx8MTc5MDEwNTUzNXww&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { identifier });
      setForgotMsg(data.message);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      login(data.token, data.user);
      navigate(data.user.role === "superadmin" ? "/admin/dashboard" : data.user.role === "customer" ? "/musteri/dashboard" : data.user.role === "viewer" ? "/izleyici" : data.user.role === "assigner" ? "/atayici" : data.user.role === "uretici" ? "/uretici" : data.user.role === "taseron" ? "/taseron" : "/tedarikci/dashboard");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F5F7FA] text-slate-900">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-white/75" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="font-heading font-extrabold text-5xl tracking-tighter">COP31 <span className="text-slate-400">Antalya</span></div>
          <p className="text-slate-600 mt-3 text-sm max-w-md">
            COP31 Antalya için tedarikçiler, müşteriler ve organizasyon arasında fiyat teklifi taleplerinin yönetildiği resmi portal.
          </p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8 relative">
        <Link
          to="/"
          data-testid="login-back-home-link"
          className="absolute top-6 left-8 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200"
        >
          ← Ana Sayfaya Dön
        </Link>
        {forgotMode ? (
          <form onSubmit={submitForgot} className="w-full max-w-sm space-y-6 fade-in">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Tedarikçi Portalı</div>
              <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-2">Şifremi Unuttum</h1>
              <p className="text-sm text-slate-500 mt-2">Kullanıcı adınızı veya kayıtlı e-posta adresinizi girin, size şifre sıfırlama bağlantısı gönderelim.</p>
            </div>
            {forgotMsg ? (
              <p className="text-emerald-600 text-sm" data-testid="forgot-success">{forgotMsg}</p>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="identifier">Kullanıcı Adı veya E-posta</Label>
                  <Input
                    id="identifier"
                    data-testid="forgot-identifier-input"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    className="rounded-none bg-white border-slate-200 h-11"
                    placeholder="kullanici-adi veya ornek@firma.com"
                    required
                  />
                </div>
                {error && <p className="text-red-500 text-sm" data-testid="forgot-error">{error}</p>}
                <Button type="submit" disabled={busy} data-testid="forgot-submit-button" className="w-full rounded-none h-11 bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
                  {busy ? "Gönderiliyor..." : "Sıfırlama Bağlantısı Gönder"}
                </Button>
              </>
            )}
            <button
              type="button"
              data-testid="back-to-login-link"
              onClick={() => { setForgotMode(false); setForgotMsg(""); setError(""); }}
              className="text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200 underline underline-offset-4"
            >
              Girişe geri dön
            </button>
          </form>
        ) : (
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-in">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Tedarikçi Portalı</div>
            <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-2">Giriş Yap</h1>
          </div>
          <div className="space-y-2">
            <Label htmlFor="username">E-posta (Kullanıcı Adı)</Label>
            <Input
              id="username"
              data-testid="login-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="rounded-none bg-white border-slate-200 h-11"
              placeholder="ornek@firma.com"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Şifre</Label>
            <div className="relative">
              <Input
                id="password"
                data-testid="login-password-input"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-none bg-white border-slate-200 h-11 pr-11"
                placeholder="••••••"
                required
              />
              <button
                type="button"
                data-testid="login-toggle-password"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900 transition-colors duration-200"
              >
                {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </div>
          {error && (
            <p className="text-red-500 text-sm" data-testid="login-error">
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit-button"
            className="w-full rounded-none h-11 bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200"
          >
            {busy ? "Giriş yapılıyor..." : "Giriş Yap"}
          </Button>
          <button
            type="button"
            data-testid="forgot-password-link"
            onClick={() => { setForgotMode(true); setError(""); }}
            className="text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200 underline underline-offset-4"
          >
            Şifremi unuttum
          </button>
        </form>
        )}
      </div>
    </div>
  );
}
